import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Inbox, CheckSquare, CalendarDays, Timer, Flame, Trophy, Compass, LogOut, Search } from "lucide-react";
import { useState } from "react";
import CaptureDialog from "@/components/CaptureDialog";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
  { to: "/everything", label: "Everything", icon: Inbox },
  { to: "/spaces", label: "Spaces", icon: Compass },
  { section: "Do" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/planner", label: "Planner", icon: CalendarDays },
  { to: "/focus", label: "Focus", icon: Timer },
  { to: "/habits", label: "Habits", icon: Flame },
  { to: "/rewards", label: "Rewards", icon: Trophy },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [captureOpen, setCaptureOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[color:var(--bg)]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/5 shrink-0 flex flex-col" data-testid="sidebar">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: "var(--orange)" }} />
            <span className="serif-italic text-lg">CortexBase</span>
          </div>
        </div>

        <button
          onClick={() => setCaptureOpen(true)}
          data-testid="capture-button"
          className="mx-4 mb-4 pill flex items-center justify-center gap-2 text-sm py-2.5"
          style={{ background: "var(--orange)", borderColor: "var(--orange)", color: "#fff" }}
        >
          + Capture
        </button>

        <button
          onClick={() => navigate("/everything?focus=search")}
          data-testid="search-shortcut"
          className="mx-4 mb-2 flex items-center gap-2 text-white/60 hover:text-white text-sm px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
        >
          <Search size={14} /> Search CortexBase…
        </button>

        <nav className="mt-4 px-3 flex-1 overflow-y-auto">
          {nav.map((item, i) => {
            if (item.section) return (
              <div key={"s" + i} className="mt-6 mb-2 px-3 text-[10px] uppercase tracking-widest text-white/30">{item.section}</div>
            );
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) => `side-item ${isActive ? "active" : ""}`}
              >
                <span className="side-dot" />
                <Icon size={15} strokeWidth={1.6} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-white/5 flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-xs">{user?.name?.[0] || "U"}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-white/40 truncate">{user?.email}</div>
          </div>
          <button onClick={logout} data-testid="logout-button" title="Sign out" className="text-white/40 hover:text-white p-1">
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative">
        <Outlet />
      </main>

      <CaptureDialog open={captureOpen} onOpenChange={setCaptureOpen} />
    </div>
  );
}
